import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import {
	ALL_IMAGE_SOURCES,
	downloadLibraryItem,
	getImageLibraryFilters,
	imageLibraryDisplayName,
	imageLibraryItemToAttachment,
	toFolderQueryParams,
	toImageSourceFilter,
	useImageLibraryKeywordsQuery,
	useImageLibraryQuery,
	useRemoveFromImageLibraryMutation,
	useRenameImageLibraryEntryMutation,
	IImageLibraryItem,
} from '../../../../queries/ImageLibrary';
import { AiButton } from '../../AiButton';
import { Button } from '../../Button';
import { FolderRail } from '../../FolderRail';
import { ImageLibraryGrid } from '../../ImageLibraryGrid';
import { Input } from '../../Input';
import { TabToggle } from '../../TabToggle';
import { s } from './styles';

interface ImageLibraryTabProps {
	onImageSelected: (image: Api.IFileAttachment) => void;
	onEditWithAI: (image: Api.IFileAttachment) => void;
	impersonationContext?: Api.IImpersonationContext;
}

export function ImageLibraryTab({ onImageSelected, onEditWithAI, impersonationContext }: ImageLibraryTabProps) {
	const [searchTerm, setSearchTerm] = React.useState('');
	const [sourceFilter, setSourceFilter] = React.useState<string>(ALL_IMAGE_SOURCES);
	const [selectedFolder, setSelectedFolder] = React.useState<string | null>(null);
	const [selectedItem, setSelectedItem] = React.useState<IImageLibraryItem>(null);
	const [pendingName, setPendingName] = React.useState('');
	const [confirmingRemove, setConfirmingRemove] = React.useState(false);

	const debouncedSearchTerm = useDebounceValue(searchTerm);

	const libraryQuery = useImageLibraryQuery({
		impersonationContext,
		kind: 'image',
		query: debouncedSearchTerm,
		source: toImageSourceFilter(sourceFilter),
		...toFolderQueryParams(selectedFolder),
	});

	const keywordsQuery = useImageLibraryKeywordsQuery({ impersonationContext, kind: 'image' });

	const renameMutation = useRenameImageLibraryEntryMutation({
		onError: () => toast.error('Could not rename this image. Try again.'),
		onSuccess: updated => {
			setSelectedItem(updated);
			toast.success('Image renamed');
		},
	});

	const removeMutation = useRemoveFromImageLibraryMutation({
		onError: () => toast.error('Could not remove this image from the library. Try again.'),
		onSuccess: () => {
			setConfirmingRemove(false);
			setSelectedItem(null);
			toast.success('Removed from your library');
		},
	});

	const items = libraryQuery.data?.pages?.flatMap(page => page.values ?? []) ?? [];
	const isFiltered = !!debouncedSearchTerm || sourceFilter !== ALL_IMAGE_SOURCES || !!selectedFolder;

	const selectItem = (item: IImageLibraryItem) => {
		setSelectedItem(item);
		setPendingName(imageLibraryDisplayName(item));
		setConfirmingRemove(false);
	};

	const renderControls = () => (
		<div className={css(s.controls)}>
			<Input
				autoComplete='off'
				id='image-library-search'
				onChange={e => setSearchTerm(e.target.value)}
				placeholder='Search your images'
				styles={[s.searchInput]}
				type='text'
				value={searchTerm}
			/>
			<TabToggle
				items={getImageLibraryFilters(false)}
				onValueChange={setSourceFilter}
				size='small'
				value={sourceFilter}
			/>
		</div>
	);

	const renderPreview = () => {
		const isBusy = renameMutation.isPending || removeMutation.isPending;
		const nameChanged = pendingName.trim() && pendingName.trim() !== imageLibraryDisplayName(selectedItem);
		const attachment = imageLibraryItemToAttachment(selectedItem);

		return (
			<div className={css(s.previewSection)}>
				<div className={css(s.previewImageContainer)}>
					<img alt={imageLibraryDisplayName(selectedItem)} className={css(s.previewImage)} src={selectedItem.url} />
				</div>
				<div className={css(s.previewNameRow)}>
					<Input
						id='image-library-name'
						onChange={e => setPendingName(e.target.value)}
						placeholder='Name this image'
						styles={[s.previewNameInput]}
						type='text'
						value={pendingName}
					/>
					<Button
						disabled={!nameChanged || isBusy}
						isLoading={renameMutation.isPending}
						onClick={() =>
							renameMutation.mutate({
								displayName: pendingName.trim(),
								impersonationContext,
								itemId: selectedItem.id,
							})
						}
						variant='secondary'
					>
						Rename
					</Button>
				</div>
				{confirmingRemove ? (
					<div className={css(s.previewActions)}>
						<span className={css(s.confirmPrompt)}>
							Remove this from your library? Content already using it keeps working.
						</span>
						<Button disabled={isBusy} onClick={() => setConfirmingRemove(false)} variant='secondary'>
							Keep it
						</Button>
						<Button
							isLoading={removeMutation.isPending}
							onClick={() => removeMutation.mutate({ impersonationContext, itemId: selectedItem.id })}
							variant='destructive'
						>
							Remove
						</Button>
					</div>
				) : (
					<div className={css(s.previewActions)}>
						<Button
							disabled={isBusy}
							onClick={() => setConfirmingRemove(true)}
							styles={[s.removeAction]}
							variant='destructive-secondary'
						>
							Remove from library
						</Button>
						<Button
							disabled={isBusy}
							onClick={() =>
								downloadLibraryItem(selectedItem).catch(() => toast.error('Could not download this image. Try again.'))
							}
							variant='secondary'
						>
							Download
						</Button>
						<Button disabled={isBusy} onClick={() => setSelectedItem(null)} variant='secondary'>
							Back
						</Button>
						<AiButton onClick={() => onEditWithAI(attachment)} title='Edit with AI' />
						<Button disabled={isBusy} onClick={() => onImageSelected(attachment)} variant='primary'>
							Add Image
						</Button>
					</div>
				)}
			</div>
		);
	};

	if (selectedItem) {
		return <div className={css(s.container)}>{renderPreview()}</div>;
	}

	return (
		<div className={css(s.container)}>
			{renderControls()}
			<FolderRail
				folders={keywordsQuery.data ?? []}
				onSelectFolder={setSelectedFolder}
				selectedFolder={selectedFolder}
			/>
			<div className={css(s.results)}>
				<ImageLibraryGrid
					emptyDescription={
						isFiltered
							? 'Try a different search term or filter.'
							: 'Check "Save to my images" when you add a photo and it will show up here for you and your teammates.'
					}
					emptyTitle={isFiltered ? 'No matching images' : 'Your library is empty'}
					hasNextPage={libraryQuery.hasNextPage}
					isFetchingNextPage={libraryQuery.isFetchingNextPage}
					isError={libraryQuery.isError}
					isLoading={libraryQuery.isLoading}
					items={items}
					onRetry={() => libraryQuery.refetch()}
					onItemClick={selectItem}
					onLoadMore={() => libraryQuery.fetchNextPage()}
				/>
			</div>
		</div>
	);
}
